<template>
    <grant-sub-wizard>
        <grant-sub-wizard-tab :before-change="() => validate()">
            <form>
                <div class="row mt-3">
                    <div class="col-lg-9 count-titles">
                        <!-- Project title -->
                        <h4 class="card-title">{{ $t('proposals.field_title__project_title') }}</h4>
                        <div class="form-group">
                            <textarea
                                class="form-control"
                                name="fp_project.title"
                                v-model="CallForm.model.fp_project.title"
                                v-validate.persist="'required'"
                                :disabled="!CallForm.isFPEditable"
                                :data-vv-as="$t('proposals.field_title__project_title')"
                                :label="$t('proposals.field_title__project_title')"
                                :error="getError('fp_project.title')"
                                :placeholder="$t('general.general__insert_here')"
                                >
                            </textarea>
                            <label v-if="getError('fp_project.title')" class="error text-danger">{{ getError('fp_project.title') }}</label>
                        </div>

                        <!-- Region -->
                        <h4 class="card-title" :title="$t('proposals.field_info__region')">
                            {{ $t('proposals.field_title__region') }}
                            <i class="fa fa-question"><span>{{ $t('proposals.field_info__region') }}</span></i>
                        </h4>
                        <div class="form-group">
                            <select
                                class="form-control"
                                v-validate.persist="'required'"
                                id="regionSelect"
                                name="fp_project.region"
                                @change="setRegion"
                                :disabled="!CallForm.isFPEditable"
                                :error="getError('fp_project.region')"
                                :data-vv-as="$t('proposals.field_title__region')"
                                >
                                    <option value="" selected="selected">{{ $t('general.general__please_select') }}</option>
                                    <option
                                        v-if="region.code !== 'O'"
                                        :value="region.regionId"
                                        :selected="CallForm.regionId === region.regionId"
                                        v-for="region in regions"
                                        :key="region.regionId"
                                        >
                                        {{region.code}} - {{region.name}}
                                    </option>
                            </select>
                            <label v-if="getError('fp_project.region')" class="error text-danger">{{getError('fp_project.region')}}</label>
                        </div>

                        <!-- Geographic scope -->
                        <h4 class="card-title" :title="$t('proposals.field_info__geographic_scope')">
                            {{ $t('proposals.field_title__geographic_scope') }}
                            <i class="fa fa-question"><span>{{ $t('proposals.field_info__geographic_scope') }}</span></i>
                        </h4>
                        <div class="form-group">
                            <textarea
                                class="form-control"
                                name="fp_project.geographic_scope"
                                v-model="CallForm.model.fp_project.geographic_scope"
                                v-validate.persist="'required'"
                                :disabled="!CallForm.isFPEditable"
                                :data-vv-as="$t('proposals.field_title__geographic_scope')"
                                :label="$t('proposals.field_title__geographic_scope')"
                                :error="getError('fp_project.geographic_scope')"
                                :placeholder="$t('general.general__insert_here')"
                                >
                            </textarea>
                            <label v-if="getError('fp_project.geographic_scope')" class="error text-danger">{{ getError('fp_project.geographic_scope') }}</label>
                        </div>

                        <!-- Start & End Dates -->
                        <div class="row">
                            <div class="col-lg-6">
                                <h4 class="card-title">
                                    {{ $t('proposals.field_title__start_date') }}
                                    <i class="fa fa-question"><span>{{ $t('proposals.field_info__start_date') }}</span></i>
                                </h4>
                                <base-input
                                    name="fp_project.start_date"
                                    v-model="CallForm.model.fp_project.start_date"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :data-vv-as="$t('proposals.field_title__start_date')"
                                    :placeholder="$t('proposals.field_title__start_date')"
                                    >
                                        <el-date-picker
                                            type="date"
                                            v-model="start_date"
                                            default-value="2021-07-01"
                                            :picker-options="startDatePickerOptions"
                                            :disabled="!CallForm.isFPEditable"
                                            :placeholder="$t('proposals.field_title__start_date')"
                                            >
                                        </el-date-picker>
                                </base-input>
                                <label v-if="getError('fp_project.start_date')" class="error text-danger">{{ getError('fp_project.start_date') }}</label>
                            </div>
                            <div class="col-lg-6">
                                <h4 class="card-title">
                                    {{ $t('proposals.field_title__end_date') }}
                                    <i class="fa fa-question"><span>{{ $t('proposals.field_info__end_date') }}</span></i>
                                </h4>
                                <base-input
                                    name="fp_project.end_date"
                                    v-model="CallForm.model.fp_project.end_date"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :placeholder="$t('proposals.field_title__end_date')"
                                    :data-vv-as="$t('proposals.field_title__end_date')"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-model="end_date"
                                        default-value="2021-07-01"
                                        :picker-options="endDatePickerOptions"
                                        :disabled="!CallForm.isFPEditable"
                                        :placeholder="$t('proposals.field_title__end_date')"
                                        >
                                    </el-date-picker>
                                </base-input>
                                <label v-if="getError('fp_project.end_date')" class="error text-danger">{{ getError('fp_project.end_date') }}</label>
                            </div>
                        </div>

                        <!-- Duration -->
                        <h4 class="card-title" :title="$t('proposals.field_info__duration')">
                            {{ $t('proposals.field_title__duration') }}
                            <i class="fa fa-question"><span>{{ $t('proposals.field_info__duration') }}</span></i>
                        </h4>
                        <div class="form-group">
                            <input
                                type="number"
                                class="form-control"
                                name="fp_project.duration"
                                min="0"
                                max="19"
                                :disabled="!CallForm.isFPEditable"
                                v-model="CallForm.model.fp_project.duration"
                                v-validate.persist="'required'"
                                :data-vv-as="$t('proposals.field_title__duration')"
                                />
                            <label v-if="getError('fp_project.duration')" class="error text-danger">{{ getError('fp_project.duration') }}</label>
                        </div>

                        <!-- Main objective -->
                        <h4 class="card-title" :title="$t('proposals.field_info__main_objective')">
                            {{ $t('proposals.field_title__main_objective') }}
                            <i class="fa fa-question"><span>{{ $t('proposals.field_info__main_objective') }}</span></i>
                        </h4>
                        <div class="mt-3">
                            <radio
                                name="fp_project.main_objective"
                                v-validate.persist="'required'"
                                value="1"
                                v-model="CallForm.model.fp_project.main_objective"
                                :disabled="!CallForm.isFPEditable"
                                :data-vv-as="$t('proposals.field_title__main_objective')"
                            >
                                <label>{{ $t('proposals.field_title__main_objective_checkbox_01') }}</label>
                            </radio>
                            <radio
                                name="fp_project.main_objective"
                                v-validate.persist="'required'"
                                value="2"
                                v-model="CallForm.model.fp_project.main_objective"
                                :disabled="!CallForm.isFPEditable"
                                :data-vv-as="$t('proposals.field_title__main_objective')"
                            >
                                <label>{{ $t('proposals.field_title__main_objective_checkbox_02') }}</label>
                            </radio>
                            <radio
                                name="fp_project.main_objective"
                                v-validate.persist="'required'"
                                value="3"
                                v-model="CallForm.model.fp_project.main_objective"
                                :disabled="!CallForm.isFPEditable"
                                :data-vv-as="$t('proposals.field_title__main_objective')"
                            >
                                <label>{{ $t('proposals.field_title__main_objective_checkbox_03') }}</label>
                            </radio>
                        </div>
                        <label v-if="getError('fp_project.main_objective')" class="error text-danger">{{ getError('fp_project.main_objective') }}</label>

                        <!-- Second objective -->
                        <h4 class="card-title" :title="$t('proposals.field_info__second_objective')">
                            {{ $t('proposals.field_title__second_objective') }}
                            <i class="fa fa-question"><span>{{ $t('proposals.field_info__second_objective') }}</span></i>
                        </h4>
                        <div class="form-group">
                            <base-checkbox
                                name="checkbox"
                                v-model="CallForm.model.fp_project.second_objective_01"
                                :disabled="!CallForm.isFPEditable"
                                >
                                {{ $t('proposals.field_title__second_objective_checkbox_01') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                v-model="CallForm.model.fp_project.second_objective_02"
                                :disabled="!CallForm.isFPEditable"
                                >
                                {{ $t('proposals.field_title__second_objective_checkbox_02') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                v-model="CallForm.model.fp_project.second_objective_03"
                                :disabled="!CallForm.isFPEditable"
                                >
                                {{ $t('proposals.field_title__second_objective_checkbox_03') }}
                            </base-checkbox>
                        </div>

                        <!-- Requested Grant Amount -->
                        <h4 class="card-title">
                            {{ $t('proposals.field_title__requested_grant_amount') }}
                            <i class="fa fa-question"><span>{{ $t('proposals.field_info__requested_grant_amount') }}</span></i>
                        </h4>
                        <div class="form-group">
                            <currency-input
                                class="form-control"
                                name="fp_project.grant_amount"
                                v-model="CallForm.model.fp_project.grant_amount"
                                currency="EUR"
                                locale="de"
                                v-validate.persist="'required'"
                                @blur="onBlur"
                                placeholder=""
                                :disabled="!CallForm.isFPEditable"
                                :value-range="{min: 0, max: 60000}"
                                :data-vv-as="$t('proposals.field_title__requested_grant_amount')"
                            />
                            <label v-if="getError('fp_project.grant_amount')" class="error text-danger">{{ getError('fp_project.grant_amount') }}</label>
                        </div>

                        <!-- Project summary -->
                        <h4 class="card-title mb-0">
                            {{ $t('proposals.field_title__project_summary') }}
                        </h4>
                        <p>{{ $t('stage_two.general_words_remaining', {words: project_summary_count}) }}</p>
                        <div class="form-group">
                            <textarea
                                class="form-control"
                                name="fp_project.project_summary"
                                maxwords="150"
                                v-wordscount="project_summary_count"
                                v-model="CallForm.model.fp_project.project_summary"
                                v-validate.persist="'required'"
                                :disabled="!CallForm.isFPEditable"
                                :data-vv-as="$t('proposals.field_title__project_summary')"
                                :label="$t('proposals.field_title__project_summary')"
                                :error="getError('fp_project.project_summary')"
                                :placeholder="$t('general.general__insert_here')"
                                >
                            </textarea>
                            <label v-if="getError('fp_project.project_summary')" class="error text-danger">{{ getError('fp_project.project_summary') }}</label>
                        </div>

                        <!-- Main Area of intervention -->
                        <h4 class="card-title">
                            {{ $t('proposals.field_title__main_area_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="coastal"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_coastal') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="marine"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_marine') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="forest"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_forest') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="terrestrial"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_terrestrial') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="fresh_water"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_fresh_water') }}</label>
                                </radio>
                            </div>
                            <div class="col-7">
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="ecosystem_services_management"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_ecosystem_services_management') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="species"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_species') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="protected_areas"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_protected_areas') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="agroforestry"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_agroforestry') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_area"
                                    value="circular_economy"
                                    v-model="CallForm.model.fp_project.main_intervention_area"
                                    v-validate.persist="'required'"
                                    :data-vv-as="$t('proposals.field_title__main_area_of_intervention')"
                                    :disabled="!CallForm.isFPEditable"
                                >
                                    <label>{{ $t('proposals.field_title__main_area_of_intervention_circular_economy') }}</label>
                                </radio>
                            </div>
                        </div>
                        <label v-if="getError('fp_project.main_intervention_area')" class="error text-danger">{{ getError('fp_project.main_intervention_area') }}</label>

                        <!-- Other Area of intervention -->
                        <h4 class="card-title">
                            {{ $t('proposals.field_title__other_area_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <base-checkbox
                                    name="checkbox"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_coastal"
                                    :disabled="!CallForm.isFPEditable"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_coastal') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_marine"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_marine') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_forest"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_forest') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_terrestrial"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_terrestrial') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_fresh_water"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_fresh_water') }}
                                </base-checkbox>
                            </div>
                            <div class="col-7">
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_ecosystem_services_management"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_ecosystem_services_management') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_species"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_species') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_protected_areas"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_protected_areas') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_agroforestry"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_agroforestry') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!CallForm.isFPEditable"
                                    v-model="CallForm.model.fp_project.other_area_of_intervention_circular_economy"
                                    >
                                    {{ $t('proposals.field_title__other_area_of_intervention_circular_economy') }}
                                </base-checkbox>
                            </div>
                        </div>

                        <!-- Main Type of intervention -->
                        <h4 class="card-title">
                            {{ $t('proposals.field_title__main_type_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <radio
                                    name="fp_project.main_intervention_type"
                                    value="conservation"
                                    v-model="CallForm.model.fp_project.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :data-vv-as="$t('proposals.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_type_of_intervention_conservation') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_type"
                                    value="restoration"
                                    v-model="CallForm.model.fp_project.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :data-vv-as="$t('proposals.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_type_of_intervention_restoration') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_type"
                                    value="threat_management"
                                    v-model="CallForm.model.fp_project.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :data-vv-as="$t('proposals.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_type_of_intervention_threat_management') }}</label>
                                </radio>
                            </div>
                            <div class="col-7">
                                <radio
                                    name="fp_project.main_intervention_type"
                                    value="awareness_education"
                                    v-model="CallForm.model.fp_project.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :data-vv-as="$t('proposals.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_type_of_intervention_awareness_education') }}</label>
                                </radio>
                                <radio
                                    name="fp_project.main_intervention_type"
                                    value="other"
                                    v-model="CallForm.model.fp_project.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!CallForm.isFPEditable"
                                    :data-vv-as="$t('proposals.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('proposals.field_title__main_type_of_intervention_other') }}</label>
                                </radio>
                            </div>
                            <input
                                type="text"
                                class="form-control mt-3"
                                name="fp_project.main_intervention_type_other"
                                v-if="CallForm.model.fp_project.main_intervention_type === 'other'"
                                v-model="CallForm.model.fp_project.main_intervention_type_other"
                                v-validate.persist="'required'"
                                :data-vv-as="$t('proposals.field_title__main_type_of_intervention_other')"
                                :disabled="!CallForm.isFPEditable"
                                />
                            <label v-if="getError('fp_project.main_intervention_type_other')" class="error text-danger">{{ getError('fp_project.main_intervention_type_other') }}</label>
                        </div>
                        <label v-if="getError('fp_project.main_intervention_type')" class="error text-danger">{{ getError('fp_project.main_intervention_type') }}</label>

                        <!-- Other Type of intervention -->
                        <h4 class="card-title">
                            {{ $t('proposals.field_title__other_type_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <base-checkbox
                                    name="checkbox"
                                    v-model="CallForm.model.fp_project.other_type_of_intervention_conservation"
                                    :disabled="!CallForm.isFPEditable"
                                    >
                                    {{ $t('proposals.field_title__other_type_of_intervention_conservation') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    v-model="CallForm.model.fp_project.other_type_of_intervention_restoration"
                                    :disabled="!CallForm.isFPEditable"
                                    >
                                    {{ $t('proposals.field_title__other_type_of_intervention_restoration') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    v-model="CallForm.model.fp_project.other_type_of_intervention_threat_management"
                                    :disabled="!CallForm.isFPEditable"
                                    >
                                    {{ $t('proposals.field_title__other_type_of_intervention_threat_management') }}
                                </base-checkbox>
                            </div>
                            <div class="col-7">
                                <base-checkbox
                                    name="checkbox"
                                    v-model="CallForm.model.fp_project.other_type_of_intervention_awareness_education"
                                    :disabled="!CallForm.isFPEditable"
                                    >
                                    {{ $t('proposals.field_title__other_type_of_intervention_awareness_education') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    v-model="CallForm.model.fp_project.other_type_of_intervention_other"
                                    :disabled="!CallForm.isFPEditable"
                                    >
                                    {{ $t('proposals.field_title__other_type_of_intervention_other') }}
                                </base-checkbox>
                            </div>
                            <input
                                type="text"
                                class="form-control mt-3"
                                name="fp_project.other_type_of_intervention_other_text"
                                v-if="CallForm.model.fp_project.other_type_of_intervention_other"
                                v-model="CallForm.model.fp_project.other_type_of_intervention_other_text"
                                v-validate.persist="'required'"
                                :data-vv-as="$t('proposals.field_title__other_type_of_intervention_other')"
                                :disabled="!CallForm.isFPEditable"
                                />
                            <label v-if="getError('fp_project.other_type_of_intervention_other_text')" class="error text-danger">{{ getError('fp_project.other_type_of_intervention_other_text') }}</label>
                        </div>

                    </div>
                </div>
            </form>
        </grant-sub-wizard-tab>
    </grant-sub-wizard>
</template>
<script>
import { DatePicker } from 'element-ui';
import Multiselect from 'vue-multiselect';
import { CallForm, RegionService } from "@/services";
import {GrantSubWizard, GrantSubWizardTab} from '@/components';

export default
{
    components:
    {
        [DatePicker.name]: DatePicker,
        Multiselect,
        GrantSubWizard,
        GrantSubWizardTab,
    },
    data()
    {
        return {
            CallForm,
            start_date: '',
            end_date: '',
            moment: '',
            startDatePickerOptions:
            {
                disabledDate (date)
                {
                    return date < new Date(2021, 6, 1) || date > new Date(2023, 2, 28);
                }
            },
            endDatePickerOptions:
            {
                disabledDate (date)
                {
                    return date < new Date(2021, 6, 1) || date > new Date(2023, 2, 28);
                }
            },
            project_summary_count: 150,
            regions: [],
            options: [],
            value: []
        }
    },
    async beforeMount()
    {
        const vm = this;
        let loader = vm.$loading.show();

        vm.moment = require('moment');

        RegionService.getApplicationRegionsWithCountries().then((response) =>
        {
            vm.regions = response.data;

            loader.hide();
        })

        if (CallForm.model.fp_project.start_date !== undefined)
        {
            vm.start_date = CallForm.model.fp_project.start_date;
        }

        if (CallForm.model.fp_project.end_date !== undefined)
        {
            vm.end_date = CallForm.model.fp_project.end_date;
        }
    },
    methods:
    {
        getError(fieldName)
        {
            return this.errors.first(fieldName);
        },
        validate()
        {
            return this.$validator.validateAll().then(res =>
            {
                this.$emit('on-validated', res, this.model);
                return res;
            });
        },
        setRegion(event)
        {
            
            CallForm.regionId = parseInt(event.target.value);
        },

        onBlur(ev)
        {
            if (ev.target.value > 60000)
            {
                // ev.target.value = 60000;
                CallForm.model.fp_project.grant_amount = 60000;
            }
        },

        changeForseenDistribution(ev)
        {
            const value = ev.target.value;
            let total =
                (+CallForm.model.fp_project.foreseen_distribution_human_resources || 0) +
                (+CallForm.model.fp_project.foreseen_distribution_human_travel || 0) +
                (+CallForm.model.fp_project.foreseen_distribution_human_equipment_and_supplies || 0) +
                (+CallForm.model.fp_project.foreseen_distribution_human_other_costs_and_services || 0);

            total = total - value;

            if (ev.target.value > Math.max(0, 100 - total))
            {
                ev.target.value = Math.max(0, 100 - total);
            }
        }
    },
    watch:
    {
        start_date(newValue, oldValue)
        {
            let date = this.moment(newValue).format('YYYY-MM-DD');

            CallForm.model.fp_project.start_date = (date === 'Invalid date' ? null : date);
        },
        end_date(newValue, oldValue)
        {
            let date = this.moment(newValue).format('YYYY-MM-DD');

            CallForm.model.fp_project.end_date = (date === 'Invalid date' ? null : date);
        }
    }
}
</script>
<style lang="scss" scoped>
    a
    {
        color: black;

        &:hover
        {
            text-decoration: underline;
        }
    }

    .count-titles
    {
        counter-reset: cardTitles;

        h4.card-title
        {
            margin-top: 10px;
            counter-increment: cardTitles;

            &:before
            {
                content: counter(cardTitles) '. ';
            }

            .fa-question
            {
                position: relative;
                margin-left: 10px;
                display: inline-block;
                width: 24px;
                height: 24px;
                font-size: 13px;
                line-height: 24px;
                text-align: center;
                color: #fff;
                border-radius: 100%;
                background-color: #2083f7;
            }
        }
    }

    .table-bordered th,
    .table-bordered td
    {
        padding: 5px;
    }

    .card-wizard .disabled
    {
        display: block;
    }
</style>
